import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import { mapCustomerApiToUi, mapDashboardToKpis } from "@/lib/mappers/customers";
import { mockCustomers, type Customer } from "@/lib/mock-customers";
import type { CustomerUI, CustomersKpisUI } from "@/types/ui/customers";
import type { CustomersApiResponse, CustomersDashboardApi } from "@/types/api/customers";

export interface GetCustomersParams extends Record<string, string | number | boolean | undefined> {
  page?: number;
  pageSize?: number;
  search?: string;
  riskLevel?: string;
  currency?: string;
  paymentTerms?: string;
  collectionStrategyId?: string;
  outstandingMin?: number;
  outstandingMax?: number;
  overdueMin?: number;
  overdueMax?: number;
  creditLimitMin?: number;
  creditLimitMax?: number;
  dsoMin?: number;
  dsoMax?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

/**
 * Compute KPIs from mock customers data
 */
function computeMockCustomersKpis(customers: CustomerUI[]): CustomersKpisUI {
  const totalCustomers = customers.length;
  const totalOutstanding = customers.reduce((sum, c) => sum + c.outstanding, 0);
  const totalOverdue = customers.reduce((sum, c) => sum + c.overdue, 0);
  const highRiskCount = customers.filter((c) => c.risk === "high").length;
  return { totalCustomers, totalOutstanding, totalOverdue, highRiskCount };
}

/**
 * Convert mock customers to UI format
 * Mock customers already have most fields, just need to ensure they match CustomerUI
 */
function convertMockCustomersToUI(mockCustomers: Customer[]): CustomerUI[] {
  return mockCustomers.map((customer) => ({
    id: customer.id,
    name: customer.name,
    accountNumber: customer.accountNumber,
    currency: customer.currency,
    outstanding: customer.outstanding,
    overdue: customer.overdue,
    creditLimit: customer.creditLimit,
    dso: customer.dso,
    risk: customer.risk,
    lastPayment: customer.lastPayment,
    contact: customer.contact,
    phone: customer.phone,
    address: customer.address,
    paymentTerms: customer.paymentTerms,
    collectionStrategyId: customer.collectionStrategyId,
    creditControllerId: customer.creditControllerId,
    accountManagerId: customer.accountManagerId,
    otherCompaniesCount: customer.otherCompaniesCount,
  }));
}

/**
 * Hook to fetch customers list data
 * Handles both Demo and Live modes
 */
export function useCustomersList(params?: GetCustomersParams) {
  const { apiGet } = useApi();
  const mockCustomersUI = convertMockCustomersToUI(mockCustomers);

  return useModeAwareQuery<CustomerUI[], Error>(
    ["customers", params],
    async ({ signal }) => {
      const response = await apiGet<CustomersApiResponse>("/api/v1/customers", params, signal);
      return response.data.map(mapCustomerApiToUi);
    },
    mockCustomersUI,
    {
      staleTime: 0, // Always consider data stale to ensure fresh fetches
      refetchOnMount: 'always', // Always refetch when component mounts
    }
  );
}

/**
 * Hook to fetch customers dashboard KPIs
 * Handles both Demo and Live modes
 */
export function useCustomersDashboard() {
  const { apiGet } = useApi();
  const mockCustomersUI = convertMockCustomersToUI(mockCustomers);
  const mockKpis = computeMockCustomersKpis(mockCustomersUI);

  return useModeAwareQuery<CustomersKpisUI, Error>(
    ["customers-dashboard"],
    async ({ signal }) => {
      const response = await apiGet<CustomersDashboardApi>("/api/v1/customers/dashboard", undefined, signal);
      return mapDashboardToKpis(response);
    },
    mockKpis,
    {
      staleTime: 0, // Always consider data stale to ensure fresh fetches
      refetchOnMount: 'always', // Always refetch when component mounts
    }
  );
}

/**
 * Combined hook for customers page data
 * Fetches both customers list and dashboard KPIs
 */
export function useCustomersPageData(params?: GetCustomersParams) {
  const customersQuery = useCustomersList(params);
  const dashboardQuery = useCustomersDashboard();

  return {
    customers: customersQuery.data ?? [],
    kpis: dashboardQuery.data ?? {
      totalCustomers: 0,
      totalOutstanding: 0,
      totalOverdue: 0,
      highRiskCount: 0,
    },
    isLoading: customersQuery.isLoading || dashboardQuery.isLoading,
    error: customersQuery.error || dashboardQuery.error,
    refetch: () => {
      customersQuery.refetch();
      dashboardQuery.refetch();
    },
  };
}
